import { Link } from 'wouter';
import React from 'react';
import Footer from './components/Footer';

const logoHeader = '/assets/ALWAYS_logo_Web_RGB_POS.png';
const logoFooter = '/assets/ALWAYS_logo_Web_RGB_NEG.png';

// Blog posts data
export const posts = [
  {
    id: 7,
    slug: "preparing-for-the-future-of-home-care-v3",
    title: "Preparing for the Future of Home Care: Introducing Care Plan Manager V3.0",
    date: "July 2026",
    excerpt: "Home care is entering a new chapter. Care Plan Manager V3.0 is our most significant platform update to date — built to reduce administrative burden, strengthen governance and give care teams more time to focus on the people they support.",
    category: "Product Updates",
    content: `Home care is entering a new chapter.

Across Ireland, expectations around quality, governance and accountability continue to grow as providers prepare for a more structured and transparent approach to delivering home support services. At the same time, demand for care is increasing, workforce pressures remain significant and providers are expected to demonstrate consistently high standards of person-centred care.

Digital technology has an important role to play.

At Allwayswithyou, we've always believed that technology should simplify care delivery — not complicate it. Our mission is to reduce administrative burden while giving care teams better information, stronger governance and more time to focus on the people they support.

That vision has shaped the development of Care Plan Manager Version 3.0, our most significant platform update to date.

**Supporting Better Care Through Better Information**

High-quality care starts with having the right information available at the right time.

The new Client Overview brings together assessments, care plans, supporting documents, digital signatures and recent activity into a single view, giving care coordinators a complete picture of each client's journey.

This not only improves day-to-day efficiency but also strengthens continuity of care by ensuring that critical information is always visible and easily accessible.

**Built Around the Care Journey**

Version 3.0 has been redesigned around the way care is actually delivered.

Client onboarding is faster, helping new referrals move quickly into assessment and care planning. Self Assessments, Home Assessments and Reassessments now sit within one integrated workspace, making it easier for staff to manage every stage of the client's journey from a single location.

Throughout the platform, navigation has been simplified, search has been enhanced and information is presented in a clearer, more intuitive way — reducing clicks while improving confidence that nothing is overlooked.

**Strengthening Governance and Quality**

As expectations for quality assurance continue to increase across the home care sector, providers need systems that support robust governance as part of everyday practice.

Care Plan Manager V3.0 introduces enhanced care plan version control, clearer approval workflows, complete digital signature history and improved reporting dashboards. Together, these features help organisations maintain accurate records, demonstrate quality processes and provide greater operational oversight.

Rather than creating additional administration, these improvements are built directly into everyday workflows.

**Technology That Gives Time Back**

The best technology often goes unnoticed.

Many of the improvements in Version 3.0 are small individually — a cleaner interface, faster search, fewer clicks, clearer navigation — but together they remove hundreds of unnecessary actions from a typical working week.

That means less time managing software and more time coordinating care, supporting staff and engaging with clients and families.

**Continuing Our Vision for Person-Centred Care**

At Allwayswithyou, our ambition extends beyond digital record keeping.

Inspired by the World Health Organization's Integrated Care for Older People (ICOPE) framework, we're building a platform that helps providers deliver proactive, coordinated and person-centred home care while meeting the evolving expectations of the sector.

Care Plan Manager V3.0 represents another important step towards that vision — supporting providers with the tools they need to deliver safe, high-quality home care today while preparing confidently for the future.

As the home care landscape continues to evolve, we'll continue working alongside providers to ensure technology remains an enabler of better care, stronger governance and improved outcomes for every client.`
  },
  {
    id: 6,
    slug: "streamlining-home-care-latest-updates",
    title: "Streamlining Home Care: How Our Latest Updates Help Providers Worldwide Work More Efficiently",
    date: "December 2025",
    excerpt: "Home care organisations across the world are navigating a period of major transformation. At Allwayswithyou, our mission is to help providers adapt to this evolving landscape by reducing administrative burden, strengthening communication, and giving teams the tools they need to deliver high-quality care at scale.",
    category: "Product Updates",
    content: `Home care organisations across the world are navigating a period of major transformation. Expectations around governance, documentation, personalised care, and accountability are rising - not just from families and funders, but increasingly from national regulators.

In Ireland, for example, the forthcoming Health (Amendment) Home Support Providers Bill 2025 will introduce new requirements around oversight, quality assurance, and transparency. Similar regulatory shifts are emerging across Europe, the UK, and North America, reflecting a global drive to professionalise home-care services.

At Allwayswithyou, our mission is to help providers adapt to this evolving landscape by reducing administrative burden, strengthening communication, and giving teams the tools they need to deliver high-quality care at scale. Our latest platform updates were designed with these global challenges in mind.

**Helping Teams Work More Efficiently, Wherever They Operate**

Home-care organisations - whether in Ireland, the Netherlands, the UK, or beyond - face the same operational reality: dispersed teams, increasing caseloads, and complex workflows. Even small inefficiencies can create friction when multiplied across hundreds or thousands of daily interactions.

Our newest improvements streamline how staff navigate the system and access relevant information. By making daily workflows simpler and clearer, providers can reduce errors, shorten administrative time, and improve the consistency of client interactions.

This allows teams to refocus on what matters most: delivering safe, personalised care.

**Elevating Communication Across Every Client Relationship**

The first impression a family receives from a home-care organisation often comes through digital communication - especially when sharing a care plan for review or approval.

To support this, we have enhanced the clarity and professionalism of signature-related emails. Staff and families now receive communications tailored to their role and context, ensuring every touchpoint reflects the organisation's standards of care.

As global expectations around transparency and informed consent increase, these improvements support stronger, more trusted client relationships.

**Improving Transparency and Audit Readiness**

Regulators around the world are placing greater emphasis on documented processes and traceable decision-making. Providers must be able to demonstrate not just what care was planned, but who approved it, when, and why.

Our upgraded tracking capabilities help organisations maintain clear, reliable records of approvals and signatures - creating an audit trail that supports local compliance requirements, internal governance, and quality-assurance processes.

This is especially relevant as many countries introduce new frameworks for home-care oversight modeled on healthcare and social-care regulation.

**Designed to Support Growth in Every Market**

Home care is expanding globally as populations age and more clients seek care at home. Providers need digital tools that scale with them - whether they support 100 clients or 10,000.

The latest updates to Allwayswithyou strengthen:
• Reduced administrative workload
• More consistent communication and documentation
• Improved visibility across roles and regions
• Scalable workflows that adapt to local operational needs

Our platform is built to support both local providers and multinational care organisations as they navigate growth and rising operational expectations.

**Looking Ahead**

We are continuing to invest in capabilities that support evolving regulatory standards and help organisations deliver more personalised, efficient care. Upcoming enhancements include:
• Extended localisation support for teams operating across regions or countries
• Improvements in address and client-data management to meet international compliance requirements
• Advanced insights to support quality, safety, and operational decision-making

Providers worldwide are preparing for a new era of home care - one that demands stronger governance, better client communication, and more efficient digital workflows. We are committed to enabling this shift.

If you would like to learn how these updates can support your organisation, please reach out to our team or your Allwayswithyou account manager.`
  },
  {
    id: 5,
    slug: "transforming-cancer-care-structured-care-plan",
    title: "Transforming Cancer Care: The Power of a Structured Care Plan",
    date: "October 19, 2025",
    excerpt: "Managing cancer care across hospitals, clinics, and home settings requires coordination of complex medical treatments alongside daily wellbeing needs. A structured digital care plan brings clarity, consistency, and confidence to every stage of care.",
    category: "Healthcare Insights",
    content: `Cancer care is about much more than treatment — it's about supporting the whole person. Alongside complex medical interventions, daily wellbeing factors such as nutrition, hydration, rest, sleep, and skin integrity play a vital role in recovery and quality of life.

But managing all these needs across multiple professionals and settings — hospitals, clinics, and the home — can be challenging. Information is often scattered across notes, emails, and systems. That's where a structured, digital care plan brings clarity, consistency, and confidence to every stage of care.

**💡 Why a Structured Care Plan Matters in Cancer Care**

**1. Early Detection of Infection or Complications**

Cancer patients are particularly vulnerable to infection. A structured digital plan ensures regular monitoring of temperature, symptoms, and overall wellbeing, helping teams detect issues early and act quickly.

**2. Nutrition and Hydration**

Maintaining proper nutrition and hydration supports strength, immunity, and recovery. A care plan helps teams track dietary intake, hydration status, and weight trends — ensuring timely interventions when appetite decreases or dehydration risk rises.

**3. Rest and Recovery**

Rest is essential for healing. Documenting rest patterns helps care teams understand each patient's recovery rhythm, identify fatigue triggers, and provide guidance on balancing rest with gentle activity.

**4. Sleep Quality**

Restorative sleep strengthens the immune system and improves mood. Including sleep quality in the care plan allows nurses and carers to track disturbances and suggest changes — from pain management timing to relaxation strategies that improve sleep hygiene.

**5. Medication Management (Including Anti-Nausea Support)**

Cancer treatments often involve complex medication schedules. A structured care plan keeps medication timing, anti-nausea support, and side-effect management organised and transparent, reducing the risk of missed doses or duplication.

**6. Energy and Fatigue Assessment**

Fatigue is one of the most common and distressing symptoms in cancer care. Regular fatigue scoring helps clinicians tailor interventions — from physiotherapy pacing and nutrition adjustments to energy conservation techniques — ensuring recovery plans are realistic and person-centred.

**7. Skin Integrity**

Chemotherapy, radiotherapy, and reduced mobility can increase skin breakdown risk. Documenting skin assessments and care routines within the plan helps teams identify early signs of irritation, pressure injury, or infection, maintaining comfort and preventing complications.

**8. Oral Care**

Mouth sores, dryness, or pain can make eating and hydration difficult. Including oral care in the plan ensures regular assessment, documentation of discomfort, and prompt intervention to maintain nutrition and prevent infection.

**9. Confidence and Reassurance for Patients and Families**

A clear, shared plan reassures patients and families that every aspect of care — from symptom tracking to daily comfort — is managed and monitored. This transparency builds trust and empowers patients to take an active role in their care.

**10. Improved Coordination Across Teams**

Cancer care spans hospital, community, and home settings. A shared digital care plan ensures everyone — oncologists, nurses, dietitians, and support workers — sees the same up-to-date information, improving communication and continuity.

**11. Compliance and Accountability**

Every entry, update, and signature is automatically logged, creating a secure audit trail for regulatory compliance and quality assurance.

**🌐 A Smarter Way Forward**

The Allwayswithyou Care Plan Manager (CPM) brings structure and simplicity to cancer care documentation.

It transforms assessments into editable, data-driven care plans in minutes — covering both core wellbeing areas (nutrition, hydration, rest, sleep, skin, and oral care) and condition-specific needs (fatigue management, medication tracking, infection monitoring).

Each plan can be reviewed by nursing or clinical staff, digitally signed, and securely stored with version control and audit history.

By reducing paperwork and enhancing visibility across teams, the CPM gives clinicians more time for what matters most — caring for patients.

Allwayswithyou — from assessment to signed care plan, in minutes.`
  },
  {
    id: 4,
    slug: "join-pilot-programme",
    title: "Join Our Pilot Programme: Transform Care Planning from Days to Hours",
    date: "October 18, 2025",
    excerpt: "We're opening limited access to our Pilot Programme, giving selected homecare providers the opportunity to experience the next generation of digital care planning — first-hand.",
    category: "Announcements",
    content: `Homecare providers across Ireland are under increasing pressure to deliver high-quality, compliant care — all while managing growing documentation demands. The Allwayswithyou Care Plan Manager (CPM) is here to change that.

We're now opening limited access to our Pilot Programme, giving selected homecare service providers the opportunity to experience the next generation of digital care planning — first-hand.

**Why participate in the pilot?**

The CPM is a purpose-built digital platform that replaces slow, paper-based workflows with a streamlined, fully digital care planning process.

In this 12-week pilot, participating providers will see how the platform:
• Reduces care plan processing time by up to 70%
• Moves from assessment to a complete digital care plan in minutes
• Embeds digital signatures and audit trails for total compliance
• Allows nursing review and approval directly within the platform
• Delivers customisable care plan templates aligned with each provider's own structure and standards

Our aim is simple — to help homecare teams save time, improve compliance, and enhance the overall client experience.

**What the pilot includes**

Allwayswithyou provides full support throughout the pilot period, including:
• Digitisation of your existing assessment tools (SATs or internal forms)
• Configuration of your care plan templates to fit your service model
• Onboarding and training for staff
• Generation of digital care plans for approximately 50 clients
• Performance insights and feedback reports on efficiency, compliance, and user experience

You'll work directly with our team to measure real-world impact across your live operations — from data entry to final care plan approval.

**The benefits you can expect**

✅ Faster client onboarding and reduced administrative workload
✅ Consistent, high-quality care plans with built-in compliance
✅ Greater transparency for nurses, carers, and families
✅ Secure GDPR-compliant data management
✅ A scalable digital foundation for the future of your service

**Limited places available**

We're inviting a small number of homecare service providers to join this pilot phase before our full commercial rollout. Participation is free of charge and includes dedicated support from our development and clinical teams.

If your organisation is ready to modernise its care planning — from assessment to signed plan in minutes — we'd love to hear from you.

**Get in touch:**

Ger O'Keeffe
CEO, Allwayswithyou

✉️ ger@allwayswithyou.com
📞 +353 87 9960456`
  },
  {
    id: 3,
    slug: "transition-paper-to-digital-care-plans",
    title: "How to Transition from Paper-Based to Digital Care Plans — A Practical Guide",
    date: "October 18, 2025",
    excerpt: "Moving to a digital care planning system doesn't have to be daunting. With the right structure, training, and support, the transition can be smooth — and the benefits are felt almost immediately.",
    category: "Best Practices",
    content: `For many homecare providers, paper-based documentation still forms the backbone of client management. Care plans, assessments, and daily notes fill entire filing cabinets — valuable, but often hard to update, share, or audit.

Moving to a digital care planning system can seem daunting, but it doesn't have to be. With the right structure, training, and support, the transition can be smooth — and the benefits are felt almost immediately.

**1. Start with why — define your goals**

Before digitising anything, take a step back and ask: what do we want to achieve?

For most providers, the goals are clear:
• Reduce time spent on paperwork
• Improve compliance and traceability
• Standardise documentation across teams
• Ensure care plans are always current and accessible

By defining these priorities early, you can focus your digital rollout on solving real problems — not just replacing paper with screens.

**2. Choose a platform built for homecare**

Generic document tools or EHR systems often don't match the day-to-day needs of homecare teams.

That's where purpose-built platforms like the Allwayswithyou Care Plan Manager (CPM) make the difference.

The CPM was designed specifically for homecare workflows, helping providers move from assessment to a complete care plan in minutes — with digital signatures, nursing review, and built-in version control for compliance.

Choose a system that mirrors your real-world process rather than forcing you to adapt to it.

**3. Digitise your existing templates**

You don't have to start from scratch. Begin by uploading or recreating your current paper templates inside the system.

The CPM allows full template customisation, so your familiar care plan structure — sections like client profile, medication, risk assessment, or health goals — can all be replicated digitally.

This approach ensures continuity for staff and a smoother learning curve.

**4. Train your team — in short, practical steps**

The most successful transitions happen when staff feel confident, not overwhelmed.

Start small:
• Begin with one region or pilot group
• Provide short, hands-on training sessions
• Use real examples and real clients
• Let staff see how much faster it is to create and update care plans digitally

When carers and nurses experience the time savings — from assessment to signed plan in minutes — adoption grows naturally.

**5. Run paper and digital in parallel (briefly)**

During the first few weeks, it's helpful to run both systems side-by-side. This ensures nothing gets lost in translation and gives teams confidence that data is safely transferred.

Once workflows are stable, the digital system can take over fully — and you'll quickly wonder how you ever managed with paper.

**6. Review, refine, and expand**

Once your digital system is up and running, review it regularly.

Are the templates capturing the right level of detail? Do your nurses find the review and sign-off process smooth?

The CPM's flexibility allows you to adjust forms, permissions, and reporting as your organisation evolves.

**The outcome: less paperwork, more care**

Transitioning to digital care plans isn't just an IT project — it's an operational upgrade.

With the Allwayswithyou Care Plan Manager, homecare providers can:
• Eliminate duplicate documentation
• Ensure every plan is compliant and version-controlled
• Give nurses and managers visibility across all clients
• Free up time for frontline staff to focus on care delivery

The change may take a few weeks, but the benefits last for years.

From paper-based to digital — faster, safer, and smarter with Allwayswithyou.`
  },
  {
    id: 1,
    slug: "introducing-care-plan-manager",
    title: "Introducing the Allwayswithyou Care Plan Manager",
    date: "October 17, 2025",
    excerpt: "Transforming how homecare providers create and manage care plans",
    category: "Product Updates",
    content: `In homecare, every minute counts. Between assessments, paperwork, and compliance requirements, care teams spend a significant portion of their day documenting rather than caring. That's why we built the Allwayswithyou Care Plan Manager (CPM) — a digital solution that transforms how homecare providers create, manage, and maintain client care plans.

**From assessments to care plan in minutes**

The CPM streamlines the entire workflow — from self-assessment and home assessment to a draft care plan in just minutes. Once assessments are uploaded, the system automatically analyses the data and generates a structured, editable care plan. This not only saves nursing staff valuable time but also ensures that every plan follows a consistent, compliant format.

**Digital signatures and clinical review built in**

Each care plan can be fully reviewed by nursing staff before sign-off. Once approved, it moves directly into the digital signature process, ensuring that both the assessor and client (or next of kin) can securely sign online — no printing, scanning, or email trails required. The result is a faster, more transparent workflow that supports HIQA and HSE compliance standards.

**Designed for every level of your organisation**

Whether you're a carer, nurse, supervisor, or quality manager, the CPM provides clear access levels and accountability for each user role. Everyone sees what they need to — and nothing they shouldn't — ensuring data security and clarity across teams.

**Tailored to your service**

Every homecare provider has its own documentation style, care plan sections, and quality standards. That's why the CPM allows customisable care plan templates to match your organisation's specific structure. From health goals and medication management to risk assessments and communication preferences, each section can be configured to fit your model of care.

**The result: more time for what matters**

With the Allwayswithyou Care Plan Manager, you can reduce administrative load, improve accuracy, and focus more on delivering care — not paperwork.

From assessment to signed care plan, in minutes.`
  },
  {
    id: 2,
    slug: "digital-care-planning-compliance",
    title: "How Digital Care Planning Improves Compliance and Efficiency in Homecare",
    date: "October 16, 2025",
    excerpt: "In the fast-paced world of homecare, managing documentation can be one of the biggest challenges for care teams.",
    category: "Industry Insights",
    content: `In the fast-paced world of homecare, managing documentation can be one of the biggest challenges for care teams. Paper-based systems and manual updates leave room for delays, missing information, and compliance risks. As regulatory standards tighten and care delivery becomes more complex, providers are turning to digital care planning to improve both efficiency and accountability — without compromising on quality.

**Turning paperwork into progress**

Traditionally, care plans were handwritten or typed, printed, and stored in binders — often becoming outdated as soon as the client's circumstances changed. With digital tools like the Allwayswithyou Care Plan Manager (CPM), this process is transformed. Assessments, care plans, and updates can be completed electronically, reducing time spent on administration and ensuring that the latest version is always available to the team.

**Compliance built into the process**

Digital care planning isn't just faster — it's smarter. The CPM automatically builds each care plan around the structure required by HSE and HIQA guidelines. Templates can be customised to align with your service's policies, ensuring that every assessment and care plan meets regulatory expectations. Built-in digital signatures and version control make audits seamless and transparent — each revision is tracked, time-stamped, and securely stored.

**Better communication, better outcomes**

Homecare involves multiple stakeholders — carers, nurses, supervisors, and family members. Digital systems make collaboration easier by allowing everyone to view up-to-date plans and updates in real time. Nursing staff can review and approve plans directly in the system, carers can log observations, and managers can track performance and compliance metrics across teams. The result is a more coordinated, person-centred approach to care.

**Efficiency that empowers staff**

For frontline teams, time is everything. The CPM helps turn assessment data into a draft care plan in minutes, not hours. This means nurses and care coordinators spend less time transcribing and more time supporting clients. Automated workflows remove repetitive tasks and reduce the risk of human error, allowing teams to focus on what matters most — delivering compassionate, high-quality care.

**The future of homecare documentation**

Digital care planning isn't about replacing people; it's about empowering them. By simplifying documentation and ensuring compliance is built in, homecare providers can deliver safer, more responsive services — all while meeting the highest standards of governance and care quality.

The Allwayswithyou Care Plan Manager helps make this future a reality — giving care teams the tools they need to stay compliant, efficient, and focused on the people they serve.`
  }
];

export default function BlogPage() {
  // Scroll to top when component mounts
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-slate-50 text-slate-900 min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link href="/" data-testid="link-home">
            <a className="flex items-center gap-2">
              <img src={logoHeader} alt="Allwayswithyou logo" className="h-8 w-auto" />
            </a>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <a href="/#value" className="hover:underline">Why</a>
            <a href="/#centre" className="hover:underline">Care Plan</a>
            <a href="/#pilot-programme" className="hover:underline">Pilot Programme</a>
            <Link href="/blog" data-testid="link-blog">
              <a className="hover:underline">Blog/News</a>
            </Link>
          </nav>
          <div className="flex items-center gap-3">
            <a href="https://cpm.allwayswithyou.com" target="_blank" rel="noopener noreferrer" data-testid="button-cpm-login" className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800">CPM Login</a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="text-white py-16" style={{ background: 'linear-gradient(135deg, #13384B 0%, #005D63 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold">News & Insights</h1>
          <p className="mt-4 text-lg text-slate-200 max-w-2xl mx-auto">
            Stay updated with the latest in care plan management, compliance updates, and industry best practices.
          </p>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            {posts.map((post) => (
              <article key={post.id} data-testid={`blog-post-${post.id}`} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-3 py-1 bg-slate-100 text-slate-700 text-xs font-medium rounded-full">
                      {post.category}
                    </span>
                    <span className="text-slate-500 text-sm">{post.date}</span>
                  </div>
                  <h2 className="text-xl font-bold mb-3 leading-tight">{post.title}</h2>
                  <p className="text-slate-600 mb-4">{post.excerpt}</p>
                  <Link href={`/blog/${post.slug}`} data-testid={`link-read-more-${post.id}`} className="text-slate-900 font-semibold hover:underline">
                    Read More →
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
